import { client } from "queryClient";
import { CardmarketCalcTask } from "types";

/** Seitengröße für "Mehr laden". */
export const CARDMARKET_CALC_TASK_PAGE_SIZE = 50;

/** "Alle laden" holt den Rest in großen Schritten statt in Fünfzigern. */
const LOAD_ALL_PAGE_SIZE = 1000;

export interface CardmarketCalcTaskPage {
  items: CardmarketCalcTask[];
  /** null, wenn keine weitere Seite existiert. */
  nextToken: string | null;
}

const pageOptions = (token: string | null, loadAll: boolean) => ({
  limit: loadAll ? LOAD_ALL_PAGE_SIZE : CARDMARKET_CALC_TASK_PAGE_SIZE,
  sortDirection: "DESC" as const,
  ...(token ? { nextToken: token } : {}),
});

/**
 * Eine Seite Online-Preisberechnungen, neueste zuerst, über den Index
 * cardmarketCalcTasks (taskType + startTime). Vorher lud die Seite die ganze
 * Tabelle (über 10.000 Zeilen) per ungeordnetem list() auf einmal.
 *
 * Abgefragt wird taskType "Scheduled": CardmarketCalcTask-Zeilen entstehen nur
 * durch den Scheduler und die Fortsetzungskette, beide schreiben "Scheduled".
 *
 * Mit loadAll wird ab token alles Verbleibende geholt.
 */
export const getCardmarketCalcTaskList = async (
  token: string | null = null,
  loadAll = false,
): Promise<CardmarketCalcTaskPage> => {
  const items: CardmarketCalcTask[] = [];
  let next = token;

  do {
    const { data, nextToken } =
      await client.models.CardmarketCalcTask.cardmarketCalcTasks(
        { taskType: "Scheduled" },
        pageOptions(next, loadAll),
      );

    items.push(...data);
    next = nextToken ?? null;
  } while (loadAll && next);

  return { items, nextToken: next };
};

/** Wie getCardmarketCalcTaskList, eingeschränkt auf eine Edition. */
export const getCardmarketCalcTaskListByExpansion = async (
  expansionID: string,
  token: string | null = null,
  loadAll = false,
): Promise<CardmarketCalcTaskPage> => {
  const items: CardmarketCalcTask[] = [];
  let next = token;

  do {
    const { data, nextToken } =
      await client.models.CardmarketCalcTask.cardmarketCalcTasksByExpansion(
        { expansionID },
        pageOptions(next, loadAll),
      );

    items.push(...data);
    next = nextToken ?? null;
  } while (loadAll && next);

  return { items, nextToken: next };
};

export const getCardmarketCalcTask = async (
  id: string,
): Promise<CardmarketCalcTask | null> => {
  const { data } = await client.models.CardmarketCalcTask.get({
    id,
  });

  return data;
};
